import { useMemo } from 'react';
import type { DailyCostBreakdown } from '@app/shared';
import { Cluster, Heading, Surface, Text } from '../ui';
import { buildCostChartModel, formatUsd, OTHER_SERIES_ID } from '../lib/costChart';

/**
 * Cost dashboard card (§14): a stacked bar chart of total cost (USD) per UTC day over
 * the window, each day's bar segmented by the agent that drove the cost (the
 * Quick-chat substrate is its own segment). One series per agent, stable colors keyed
 * to the contract's ordered `agents` list, native `<title>` tooltips + a legend.
 *
 * Rendered as dependency-free inline SVG on the `@/ui` design layer (no chart lib):
 * axis text + gridlines use `currentColor` inherited from a token-driven `text.secondary`
 * wrapper so the chart is theme-aware (light/dark) without hard-coded neutrals; only the
 * categorical series palette is fixed (colourblind-safe, never cycled — the folded
 * "Other" bucket takes a recessive neutral).
 */

/**
 * Validated categorical palette (dataviz skill reference instance). Series 1 leads with
 * the EHE brand blue (#005ff3) to tie the chart into the palette; the remaining seven
 * hues keep the colourblind-safe separation and fixed order.
 */
const SERIES_PALETTE = [
  '#005ff3', // EHE brand blue
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#e87ba4', // magenta
  '#eb6834', // orange
] as const;

/** Round a raw axis maximum up to a clean 1/2/5×10ⁿ value for readable gridlines. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

export function CostChart({
  data,
  title = 'Cost — last 14 days',
}: {
  data: DailyCostBreakdown;
  title?: string;
}) {
  const model = useMemo(() => buildCostChartModel(data), [data]);

  // Series color: the categorical hue by index, or `null` for the recessive "Other" fold.
  const colors = model.series.map((s, i) =>
    s.agentId === OTHER_SERIES_ID ? null : (SERIES_PALETTE[i % SERIES_PALETTE.length] ?? null),
  );

  const n = model.xLabels.length;
  const W = Math.max(480, n * 44);
  const H = 300;
  const M = { left: 56, right: 12, top: 12, bottom: 28 };
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const rawMax = Math.max(
    0,
    ...model.xLabels.map((_, d) => model.series.reduce((sum, s) => sum + (s.data[d] ?? 0), 0)),
  );
  const yMax = niceMax(rawMax);
  const band = n > 0 ? plotW / n : plotW;
  const barW = Math.min(band * 0.62, 40);
  const yOf = (v: number) => M.top + plotH - (v / yMax) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  return (
    <Surface bordered radius="lg" padding="5" data-testid="cost-chart" style={{ marginBottom: '1.5rem' }}>
      <Cluster justify="between" align="baseline" style={{ marginBottom: '0.5rem' }}>
        <Heading level={3} size="sm">
          {title}
        </Heading>
        <Text size="sm" tone="secondary" data-testid="cost-chart-total">
          {formatUsd(model.totalUsd)} total
        </Text>
      </Cluster>

      {model.isEmpty ? (
        <Text
          as="div"
          tone="secondary"
          align="center"
          size="sm"
          data-testid="cost-chart-empty"
          style={{ padding: '2.5rem 0' }}
        >
          No cost recorded in this window.
        </Text>
      ) : (
        // Keep bars legible on narrow screens without ever scrolling the page body.
        <div style={{ overflowX: 'auto' }} data-testid="cost-chart-plot">
          {/* text.secondary wrapper → SVG currentColor is theme-aware for axes/gridlines. */}
          <Text as="div" tone="secondary" style={{ minWidth: 480 }}>
            <svg width={W} height={H} role="img" aria-label={`${title}: stacked cost per day`}>
              {ticks.map((t, i) => (
                <g key={`y${i}`}>
                  <line
                    x1={M.left}
                    x2={W - M.right}
                    y1={yOf(t)}
                    y2={yOf(t)}
                    stroke="currentColor"
                    strokeOpacity={0.14}
                  />
                  <text
                    x={M.left - 8}
                    y={yOf(t) + 3}
                    textAnchor="end"
                    fontSize={10}
                    fill="currentColor"
                    fillOpacity={0.85}
                  >
                    {formatUsd(t)}
                  </text>
                </g>
              ))}
              {model.xLabels.map((label, d) => {
                const cx = M.left + band * d + band / 2;
                let cursorY = M.top + plotH;
                return (
                  <g key={`x${d}`}>
                    {model.series.map((s, si) => {
                      const v = s.data[d] ?? 0;
                      if (v <= 0) return null;
                      const h = (v / yMax) * plotH;
                      cursorY -= h;
                      const fill = colors[si];
                      return (
                        <rect
                          key={si}
                          x={cx - barW / 2}
                          y={cursorY}
                          width={barW}
                          height={h}
                          fill={fill ?? 'currentColor'}
                          fillOpacity={fill ? 1 : 0.4}
                        >
                          <title>{`${s.label}: ${formatUsd(v)} — ${label}`}</title>
                        </rect>
                      );
                    })}
                    <text
                      x={cx}
                      y={H - 8}
                      textAnchor="middle"
                      fontSize={10}
                      fill="currentColor"
                      fillOpacity={0.85}
                    >
                      {label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Text>

          <Cluster gap="3" style={{ marginTop: '0.5rem' }}>
            {model.series.map((s, si) => {
              const fill = colors[si];
              return (
                <Cluster key={si} gap="1" align="center" wrap={false}>
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      display: 'inline-block',
                      backgroundColor: fill ?? 'currentColor',
                      opacity: fill ? 1 : 0.4,
                    }}
                  />
                  <Text size="xs" tone="secondary">
                    {s.label}
                  </Text>
                </Cluster>
              );
            })}
          </Cluster>
        </div>
      )}
    </Surface>
  );
}
